import { useCallback, useEffect, useRef, useState } from 'react';
import apiService from '../services/ApiService';
import { useAuth } from '../contexts/AuthContext';
import {
  ONBOARDING_VERSION,
  defaultOnboardingState,
  normalizeOnboardingState,
  type OnboardingState,
} from '../utils/onboarding';
import type { AnalyticsEventName } from '../services/api/analytics';

/**
 * Loads and persists per-user onboarding state.
 * Failures never block the app — state falls back to defaults.
 */
export function useOnboarding() {
  const { isAuthenticated, user } = useAuth();
  const [state, setState] = useState<OnboardingState>(defaultOnboardingState());
  const [loading, setLoading] = useState(true);
  const trackedStart = useRef(false);
  const trackedComplete = useRef(false);

  const reload = useCallback(async () => {
    if (!isAuthenticated) {
      setState(defaultOnboardingState());
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const profile = await apiService.getUserProfile();
      setState(normalizeOnboardingState(profile?.onboarding_state));
    } catch {
      setState(defaultOnboardingState());
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const persist = useCallback(async (patch: Partial<OnboardingState>) => {
    setState((prev) => normalizeOnboardingState({ ...prev, ...patch }));
    try {
      const result = await apiService.updateOnboardingState(patch as Record<string, unknown>);
      const next = normalizeOnboardingState(result.onboarding_state);
      setState(next);
      return next;
    } catch (err) {
      console.debug('onboarding persist failed', err);
      return null;
    }
  }, []);

  const track = useCallback(
    (event: AnalyticsEventName, metadata: Record<string, unknown> = {}) => {
      void apiService.trackEvent(event, {
        metadata: {
          role: user?.role || 'user',
          onboarding_version: ONBOARDING_VERSION,
          ...metadata,
        },
      });
    },
    [user?.role]
  );

  const markStarted = useCallback(async () => {
    if (trackedStart.current || state.started_at) return;
    trackedStart.current = true;
    const started_at = new Date().toISOString();
    track('onboarding_started');
    await persist({ started_at });
  }, [persist, state.started_at, track]);

  const dismissWelcome = useCallback(async () => {
    track('onboarding_skipped', { step: 'welcome' });
    await persist({
      welcome_dismissed_at: new Date().toISOString(),
      skipped_at: new Date().toISOString(),
      last_step: 'welcome',
    });
  }, [persist, track]);

  const dismissChecklist = useCallback(async () => {
    track('onboarding_step_dismissed', { step: 'builder_checklist' });
    await persist({
      checklist_dismissed_at: new Date().toISOString(),
      last_step: 'builder_checklist',
    });
  }, [persist, track]);

  const dismissOrganizerChecklist = useCallback(async () => {
    track('onboarding_step_dismissed', { step: 'organizer_checklist' });
    await persist({ organizer_checklist_dismissed_at: new Date().toISOString() });
  }, [persist, track]);

  const dismissJudgeGuide = useCallback(async () => {
    track('onboarding_step_dismissed', { step: 'judge_guide' });
    await persist({ judge_guide_dismissed_at: new Date().toISOString() });
  }, [persist, track]);

  const dismissClubGuide = useCallback(async () => {
    track('onboarding_step_dismissed', { step: 'club_guide' });
    await persist({ club_guide_dismissed_at: new Date().toISOString() });
  }, [persist, track]);

  const dismissShopTip = useCallback(async () => {
    track('onboarding_step_dismissed', { step: 'shop_tip' });
    await persist({
      shop_tip_dismissed_at: new Date().toISOString(),
      tips_dismissed: [...state.tips_dismissed, 'shops_directory'],
    });
  }, [persist, state.tips_dismissed, track]);

  const dismissTip = useCallback(
    async (tipId: string) => {
      if (state.tips_dismissed.includes(tipId)) return;
      track('onboarding_step_dismissed', { step: tipId });
      await persist({
        tips_dismissed: [...state.tips_dismissed, tipId],
        last_step: tipId,
      });
    },
    [persist, state.tips_dismissed, track]
  );

  const markStep = useCallback(
    async (stepId: string) => {
      if (state.steps[stepId]) return;
      track('onboarding_step_completed', { step: stepId });
      await persist({
        steps: { ...state.steps, [stepId]: true },
        last_step: stepId,
      });
    },
    [persist, state.steps, track]
  );

  const markCompleted = useCallback(async () => {
    if (trackedComplete.current || state.completed_at) return;
    trackedComplete.current = true;
    track('onboarding_completed');
    await persist({
      completed_at: new Date().toISOString(),
      checklist_dismissed_at: state.checklist_dismissed_at || new Date().toISOString(),
      welcome_dismissed_at: state.welcome_dismissed_at || new Date().toISOString(),
    });
  }, [persist, state.checklist_dismissed_at, state.completed_at, state.welcome_dismissed_at, track]);

  const showSetupGuide = useCallback(async () => {
    track('onboarding_resumed', { reason: 'show_setup_guide' });
    await persist({
      checklist_dismissed_at: null,
      welcome_dismissed_at: null,
      skipped_at: null,
      completed_at: null,
      organizer_checklist_dismissed_at: null,
      judge_guide_dismissed_at: null,
      club_guide_dismissed_at: null,
      shop_tip_dismissed_at: null,
    });
  }, [persist, track]);

  const resetOnboarding = useCallback(async () => {
    try {
      const result = await apiService.updateOnboardingState({}, { reset: true });
      setState(normalizeOnboardingState(result.onboarding_state));
      trackedStart.current = false;
      trackedComplete.current = false;
      track('onboarding_restarted', { reason: 'reset' });
    } catch (err) {
      console.debug('onboarding reset failed', err);
    }
  }, [track]);

  return {
    state,
    loading,
    reload,
    persist,
    markStarted,
    dismissWelcome,
    dismissChecklist,
    dismissOrganizerChecklist,
    dismissJudgeGuide,
    dismissClubGuide,
    dismissShopTip,
    dismissTip,
    markStep,
    markCompleted,
    showSetupGuide,
    resetOnboarding,
    track,
  };
}
